/**
 * Envoltorio de MobbScan 2.8.25. Copiado de src/libs/mobbscan/mobbscan.ts de la app actual sin cambiar
 * ninguna llamada al SDK (CLAUDE.md, inventario D9): initAPI, setBaseURL, setPlaceholder,
 * enableVideoMobileCapture, setFileBrowserEnabled, setDetectionListener, start, scan, validate(FACE),
 * hideDocumentsAndShowFace y los mismos selectores del DOM del SDK. Cambios: tipos estrictos, claves
 * con prefijo VITE_ y lectura perezosa de los globales (el SDK puede no estar cargado en pruebas).
 */

export type ScanSideType = 'SCAN_ONLY_BACK' | 'SCAN_ONLY_FRONT' | 'SCAN_BOTH_SIDES'

export const MobbscanSource = {
  Webcam: 'WEBCAM',
  File: 'FILE',
  Mobile: 'MOBILE',
  Pdf: 'PDF',
} as const

export const MobbscanScaningState = {
  Started: 'STARTED',
  Capturing: 'CAPTURING',
  NotDetected: 'NOT_DETECTED',
  NotFocused: 'NOT_FOCUSED',
  Timeout: 'TIMEOUT',
  Error: 'ERROR',
  Detected: 'DETECTED',
} as const

export const MobbscanFaceMatchState = {
  Started: 'STARTED',
  Capturing: 'CAPTURING',
  Detected: 'DETECTED',
  Timeout: 'TIMEOUT',
  NotDetected: 'NOT_DETECTED',
} as const

export const MobbscanScannedResult = {
  Ok: 'OK',
  Error: 'ERROR',
} as const

export const MobbscanErrorCode = {
  WebcamDenied: 'WEBCAM_PERMISSION_DENIED',
  ConnectionError: 'CONNECTION_ERROR',
  NoWebcamFound: 'WEBCAM_NO_DEVICES_FOUND',
} as const

export const FaceMatchValidateState = {
  Valid: 'VALID',
  NotValid: 'NOT_VALID',
} as const

export interface MobbscanDetectedData {
  image: Blob
  side: 'FRONT' | 'BACK'
  source: string
}

export interface MobbscanError {
  code?: string
  description?: string
}

/** Documento leído (src/libs/mobbscan/mobbscan.types.ts, campos que usa el registro). */
export interface MobbscanDocument {
  address?: string
  personalNumber?: string
  documentNumber?: string
  documentType?: string
  name?: string
  surname?: string
  firstSurname?: string
  secondSurname?: string
  gender?: 'M' | 'F'
  dateOfExpiry?: string
  dateOfBirth?: string
  nationality?: string
  issuingState?: string
  city?: string
  region?: string
  [key: string]: unknown
}

export interface MobbscanScannedData {
  code?: string
  description?: string
  scanId?: string
  document: Partial<MobbscanDocument>
}

export interface FaceMatchValidateData {
  code: string
  description: string
  scanId: string
  type: string
  score: number
  state: string
}

const api = (): MobbScanGlobalApi => {
  if (!window.MobbScanAPI) throw new Error('MobbScan SDK not loaded')
  return window.MobbScanAPI
}

const setDisplay = (selector: string, display: string) => {
  const el = document.querySelector<HTMLElement>(selector)
  if (el) el.style.display = display
}
const click = (selector: string) => document.querySelector<HTMLElement>(selector)?.click()
const clickFirst = (selectors: string[]) => {
  const target = selectors.map((selector) => document.querySelector<HTMLElement>(selector)).find(Boolean)
  target?.click()
  return Boolean(target)
}

const hideMobbscanBack = () => setDisplay('.msw-div-BACK', 'none')
const showMobbscanBack = () => setDisplay('.msw-div-BACK', 'flex')
const hideMobbscanFront = () => setDisplay('.msw-div-FRONT', 'none')
const showMobbscanFront = () => setDisplay('.msw-div-FRONT', 'flex')
const initFrontCapture = () => click('#msw-img-FRONT')
const initBackCapture = () => click('#msw-img-BACK')
// `msw-image-FACE` es la miniatura de arranque del SDK: su listener inicia
// getUserMedia. `msw-img-FACE` es solo el lienzo de procesamiento posterior.
const initFaceMatchCapture = () => clickFirst(['#msw-image-FACE', '#msw-video-FACE', '#div-video-FACE', '#msw-img-FACE'])
const hideDocumentsAndShowFace = () => {
  const sdk = api()
  // `start()` siempre añade el contenedor vacío de cara, incluso cuando su UI aún no
  // está disponible. Hay que comprobar el disparador real, no el contenedor.
  const hasFaceTrigger = Boolean(document.querySelector('#msw-image-FACE, #msw-video-FACE, #div-video-FACE, #msw-img-FACE'))
  if (!hasFaceTrigger && currentPlaceholderId && sdk.startFaceCapture) {
    document.querySelector('#msw-div-face')?.remove()
    sdk.startFaceCapture(currentPlaceholderId)
    sdk.hideDocumentsAndShowFace()
    return
  }
  sdk.hideDocumentsAndShowFace()
}

let currentPlaceholderId: string | undefined

const mobbscanInit = ({
  placeholderId,
  listener,
  timeout,
  faceMatchListener,
}: {
  placeholderId: string
  listener: (params: { state: string; data: MobbscanDetectedData; error: MobbscanError | null }) => void
  faceMatchListener?: (state: string, data: { source: string; image: Blob }, error: unknown) => void
  timeout?: number
}) => {
  const sdk = api()
  currentPlaceholderId = placeholderId
  document.querySelector<HTMLElement>(`#${placeholderId}`)?.classList.add('mobbscan-placeholder-container')
  sdk.initAPI(import.meta.env.VITE_MOBBSCAN_API_KEY)
  sdk.setBaseURL(import.meta.env.VITE_MOBBSCAN_API_URL)
  sdk.setPlaceholder(placeholderId)
  sdk.enableVideoMobileCapture()
  sdk.setFileBrowserEnabled(true)
  sdk.setErrorAccessingCamera(() => undefined)
  if (faceMatchListener !== undefined) sdk.setFaceDetectionListener(faceMatchListener)
  if (timeout !== undefined) sdk.setDetectionTimeOut(timeout)
  sdk.setDetectionListener((state, data, error) => listener({ state, data, error }))
}

const mobbscanStart = ({ scanCode, scanSideType, callback }: { scanCode: string; scanSideType: ScanSideType; callback?: (result: unknown, data: { scanId: string }, error: unknown) => void }) => {
  document.querySelector<HTMLElement>(`#${currentPlaceholderId}`)?.classList.add('mobbscan-placeholder-container')
  api().start(scanCode, scanSideType, callback ?? (() => undefined))
}

const mobbscanScan = ({ frontImage, backImage, onScanned, onScan }: { frontImage: Blob; backImage: Blob | undefined; onScanned: (r: { result: string; data: MobbscanScannedData; error: unknown }) => void; onScan: () => void }) => {
  onScan()
  api().scan(frontImage, backImage, (result, data, error) => onScanned({ result, data: data as MobbscanScannedData, error }))
}

const validateFaceMatch = ({ image, callback }: { image: Blob; callback: (result: string, resultData: { data: FaceMatchValidateData }, error: unknown) => void }) => {
  api().validate(window.MobbScanValidationType?.FACE, image, (result, data, error) => callback(result, data as { data: FaceMatchValidateData }, error))
}

const getDocumentNumber = (document: Partial<MobbscanDocument>) => {
  if (document.documentType?.toLowerCase().includes('esp') || document.documentType === 'NIE') {
    return document.personalNumber || document.documentNumber
  }
  return document.documentNumber || document.personalNumber
}

export const Mobbscan = {
  scan: mobbscanScan,
  init: mobbscanInit,
  start: mobbscanStart,
  hideMobbscanBack,
  showMobbscanBack,
  hideMobbscanFront,
  showMobbscanFront,
  initFrontCapture,
  initBackCapture,
  hideDocumentsAndShowFace,
  validateFaceMatch,
  initFaceMatchCapture,
  getDocumentNumber,
  isLoaded: () => Boolean(window.MobbScanAPI),
}
